<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('invoice.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('invoice.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="row">
            <div class="col-md-6 d-flex">
              <ts-button outline color="info" @click="$router.push({ name: 'invoice-create' })">{{ $t('add_new')
                }}</ts-button>
              <a-select v-model="is_new_student" class="ml-2" style="width: 500px" placeholder="All" :allowClear="true"
                :options="[
                  {
                    value: 1,
                    label: 'New Student'
                  },
                  {
                    value: 0,
                    label: 'Old Student '
                  }
                ]" @change="fetchData()"></a-select>
            </div>
          </div>
          <div class="d-flex">
            <ts-input v-model="search" :placeholder="$t('search')" style="width: 250px" class="inline-block" />
            <ts-button outline class="ml-2" v-tooltip="$t('invoice.filter')" @click.prevent="showFilter">
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
        <!-- drawer -->
        <a-drawer :title="$t('invoice.filter')" placement="right" :closable="false" :visible="visible"
          @close="onCloseDrawer" :zIndex="1020" width="300px">
          <ts-loading-banner :loading="waiting_fetch">
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ $t('invoice.branch') }}</label>
                  <a-select style="width: 257px" v-model="branch_id" :options="branchs" showSearch mode="multiple"
                    :filterOption="(input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                      " :maxTagCount="1" :maxTagTextLength="17" :placeholder="$t('all')">
                  </a-select>
                </div>
              </div>
            </div>
            <program-select :companyId="companyId" @fetch="fetchData()" />
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ $t('invoice.invoiceDate') }}</label>
                  <a-range-picker format="DD-MM-YYYY" :defaultValue="rangeDate" :defaultPickerValue="rangeDate"
                    @change="dateRangeChange">
                    <template slot="renderExtraFooter"></template>
                  </a-range-picker>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ $t('invoice.cashier') }}</label>
                  <a-select style="width: 257px" v-model="created_by" :options="cashiers" showSearch mode="multiple"
                    :filterOption="(input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                      " :maxTagCount="1" :maxTagTextLength="17" :placeholder="$t('all')">
                  </a-select>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <label>{{ $t('invoice.byFeeType') }}</label>
                <a-select style="width: 257px" v-model="filter_by_fee_type" :options="feeTypes" showSearch
                  mode="multiple" :filterOption="(input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                    " :maxTagCount="1" :maxTagTextLength="17" :placeholder="$t('all')">
                </a-select>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <ts-checkbox v-model="isDeleted" @change="fetchData()">Voided</ts-checkbox>
              </div>
            </div>
          </ts-loading-banner>
        </a-drawer>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table :columns="columns" :data="resources" :loading="loading" max-height="700" stripe highlight-row
          size="small">
          <template slot-scope="{ row }" slot="branch">
            {{ row.branch ? row.branch.branch_name_en : '' }}
          </template>
          <template slot-scope="{ row }" slot="invoice">
            <p>
              <i class="fas fa-sticky-note"></i>
              {{ row.inv_number }}
            </p>
            <span v-tooltip="$t('invoice.invoiceDate')" class="ts-text-orange-500 ts-font-semibold">
              <i class="fas fa-calendar-alt"></i>
              {{ row.inv_date }}
            </span>
          </template>
          <template slot-scope="{ row }" slot="student">
            <div class="ts-py-2">
              <p class="ts-font-semibold ts-text-blue-600" v-tooltip="$t('invoice.studentName')">
                <i class="fas fa-user-graduate"></i>
                {{ row.student.full_name_en }}
              </p>
              <p v-tooltip="$t('invoice.studentId')">
                <i class="fas fa-id-card-alt"></i>
                {{ row.student.card_number }}
              </p>
              <p v-tooltip="$t('invoice.sex')">
                <i class="fas fa-transgender-alt"></i>
                {{ row.student.gender }}
              </p>
              <p v-tooltip="$t('invoice.phoneNumber')" class="ts-text-blue-500" v-if="row.student.phone">
                <i class="fas fa-mobile"></i>
                {{ row.student.phone }}
              </p>
              <p v-if="row.is_new_student">
                <span class="ts-bg-red-600 ts-px-1 ts-rounded-lg ts-text-white ts-text-xs">{{ $t('invoice.new')
                }}</span>
              </p>
              <p v-else>
                <span class="ts-bg-gray-600 ts-px-1 ts-rounded-lg ts-text-white ts-text-xs">{{ $t('invoice.old')
                }}</span>
              </p>
            </div>
          </template>
          <template slot-scope="{ row }" slot="grade">
            <p class="ts-whitespace-no-wrap">
              {{ row.grade.grade_name_foreign }}
            </p>
            <p class="ts-whitespace-no-wrap">
              {{ row.academic_term.term_name }}
            </p>
          </template>
          <template slot-scope="{ row }" slot="class">
            <p class="ts-whitespace-no-wrap">
              {{
                row.academic_class
                  ? row.academic_class.class_name
                  : null
              }}
            </p>
            <p class="ts-whitespace-no-wrap">
              {{ row.study_time }}
            </p>
            <p class="ts-whitespace-no-wrap">
              {{
                row.academic_class
                  ? row.academic_class.teacher
                    ? row.academic_class.teacher.full_name_en
                    : ''
                  : ''
              }}
            </p>
          </template>
          <template slot-scope="{ row }" slot="total_amount">
            $ {{ row.total_amount }}
          </template>
          <template slot-scope="{ row }" slot="paid_usd">
            $ {{ formatNumber(row.amount_paid_usd) }}
          </template>
          <template slot-scope="{ row }" slot="paid_khr">
            ៛ {{ formatNumber(row.amount_paid_khr) }}
          </template>
          <template slot-scope="{ row }" slot="amount_due">
            $ {{ row.amount_due }}
          </template>
          <template slot-scope="{ row }" slot="expire_date">
            <strong class="ts-text-orange-600">{{
              getExpiredDate(row.invoice_detail)
              }}</strong>
          </template>
          <template slot-scope="{ row }" slot="next_payment_date">
            <span class="ts-bg-orange-600 ts-text-white ts-font-semibold ts-rounded-lg ts-px-2" v-if="!row.is_paid">
              {{ row.next_payment_date }}
            </span>
          </template>
          <template slot-scope="{ row }" slot="remark">
            <div v-if="row.remark ? row.remark.length < 25 : true">
              {{ row.remark }}
            </div>
            <div v-tooltip="row.remark" v-else>
              {{ row.remark.substring(0, 25) + '...' }}
            </div>
          </template>
          <template slot-scope="{ row }" slot="status">
            <span v-if="!row.is_paid" class="label label-danger">Unpaid</span>
            <span v-else class="label label-success">Paid</span>
          </template>
          <template slot-scope="{ row }" slot="cashier">
            <p class="ts-whitespace-no-wrap">
              {{
                row.user
                  ? row.user.employee
                    ? row.user.employee.name_en
                    : null
                  : null
              }}
            </p>
          </template>
          <template slot-scope="{ row }" slot="action">
            <div class="ts-flex ts-space-x-2">
              <a href="#"
                class="ts-bg-blue-100 ts-text-blue-500 ts-w-8 ts-h-8 ts-flex ts-items-center ts-justify-center ts-rounded-full"
                v-tooltip="'Old/New'" @click.prevent="oldNewRowClick(row)">
                <i class="fas fa-highlighter"></i>
              </a>
              <a href="#"
                class="ts-bg-blue-100 ts-text-blue-500 ts-w-8 ts-h-8 ts-flex ts-items-center ts-justify-center ts-rounded-full"
                v-tooltip="$t('tooltip.show')" @click.prevent="show(row)">
                <i class="fas fa-eye"></i>
              </a>
              <a href="#"
                class="ts-bg-red-100 ts-text-red-500 ts-w-8 ts-h-8 ts-flex ts-items-center ts-justify-center ts-rounded-full"
                v-tooltip="$t('pdf')" @click.prevent="pdfView(row)">
                <i class="fas fa-file-pdf"></i>
              </a>
              <a href="#"
                class="ts-bg-red-100 ts-text-red-500 ts-w-8 ts-h-8 ts-flex ts-items-center ts-justify-center ts-rounded-full"
                @click.prevent="deleteRecord(row)" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </div>
          </template>
        </Table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
      <pdf-preview v-model="pdf_view" :invoice-id="invoiceId" @cancel="value => (pdf_view = value)" />
      <a-modal v-model="show_update_old_new" :title="$t('invoice.oldNew')" :footer="null" centered width="300px">
        <ts-loading-banner :loading="waiting_update_old_new">
          <div class="ts-space-y-2">
            <div class="row">
              <div class="col-md-12">
                <select class="form-control" v-model="oldNewModel.is_new_student">
                  <option :value="false">Old</option>
                  <option :value="true">New</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
                <ts-button @click="() => show_update_old_new = false">{{ $t('cancel') }}</ts-button>
                <ts-button color="primary" @click="onUpdateOldNew">{{ $t('update') }}</ts-button>
              </div>
            </div>
          </div>
        </ts-loading-banner>
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { debounce } from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import PdfPreview from './component/pdf_preview'
import ProgramSelect from './component/program_select_box'
import Reason from './component/reason_delete'

export default {
  name: 'invoice',
  components: {
    PdfPreview,
    ProgramSelect
  },
  data() {
    return {
      visible: false,
      waiting_update_old_new: false,
      show_update_old_new: false,
      waiting_fetch: false,
      waiting_fetch: false,
      loading: false,
      pdf_view: false,
      invoiceId: 0,
      branchs: [],
      cashiers: [],
      model: {
        void_reason: null
      },
      oldNewModel: {
        inv_id: null,
        is_new_student: false
      },
      errors: new Errors(),
      rangeDate: [moment(), moment()]
    }
  },
  mounted() {
    this.branch_id = [this.$store.state.authUser.default_branch_id]
    this.fetchData()
  },
  computed: {
    ...mapState('invoice', ['resources', 'pagination']),
    companyId() {
      if (this.branch_id) {
        const branch = this.branchs.filter(b => this.branch_id.includes(b.value))
        return Array.from(new Set(branch.map(el => el.companyId)))
      }
      return [12]
    },
    search: {
      get() {
        return this.$store.state.invoice.search
      },
      set(newValue) {
        this.$store.commit('invoice/SET_SEARCH', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    is_new_student: {
      get() {
        return this.$store.state.invoice.is_new_student
      },
      set(newValue) {
        this.$store.commit('invoice/SET_IS_NEW_STUDENT', newValue)
      }
    },
    isDeleted: {
      get() {
        return this.$store.state.invoice.is_deleted
      },
      set(newValue) {
        this.$store.commit('invoice/SET_IS_DELETE', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    branch_id: {
      get() {
        return this.$store.state.invoice.branch_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_BRANCH_ID', newValue)
      }
    },
    created_by: {
      get() {
        return this.$store.state.invoice.created_by
      },
      set(newValue) {
        this.$store.commit('invoice/SET_CREATED_BY', newValue)
      }
    },
    filter_by_fee_type: {
      get() {
        return this.$store.state.invoice.filter_by_fee_type
      },
      set(newValue) {
        this.$store.commit('invoice/SET_FILTER_BY_FEE_TYPE', newValue)
      }
    },
    feeTypes() {
      return [
        { value: 1, label: 'Tuition Fee' },
        { value: 2, label: 'Services Fee' },
        { value: 3, label: 'Bus Fee' },
        { value: 4, label: 'Study Material' },
        { value: 5, label: 'Other Material' },
        { value: 6, label: 'Others' }
      ]
    },
    columns() {
      return [
        {
          title: this.$t('invoice.branch'),
          width: 120,
          slot: 'branch'
        },
        {
          title: this.$t('invoice.invoice'),
          width: 150,
          slot: 'invoice'
        },
        {
          title: this.$t('invoice.student'),
          minWidth: 150,
          slot: 'student'
        },
        {
          title: this.$t('invoice.grade'),
          width: 120,
          slot: 'grade'
        },
        {
          title: this.$t('invoice.class'),
          width: 120,
          slot: 'class'
        },
        {
          title: this.$t('invoice.totalAmount'),
          width: 120,
          className: "ts-text-center",
          slot: 'total_amount'
        },
        {
          title: this.$t('invoice.paidInUsd'),
          width: 120,
          slot: 'paid_usd'
        },
        {
          title: this.$t('invoice.paidInKhr'),
          width: 120,
          slot: 'paid_khr'
        },
        {
          title: this.$t('invoice.dueAmount'),
          width: 130,
          slot: 'amount_due'
        },
        {
          title: this.$t('invoice.expireDate'),
          width: 120,
          slot: 'expire_date'
        },
        {
          title: this.$t('invoice.nextPaymentDate'),
          width: 160,
          slot: 'next_payment_date'
        },
        {
          title: this.$t('invoice.remark'),
          width: 160,
          slot: 'remark'
        },
        {
          title: this.$t('invoice.status'),
          slot: 'status',
          width: 100
        },
        {
          title: this.$t('invoice.cashier'),
          slot: 'cashier',
          width: 100
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 200
        }
        // { name: this.$t('invoice.remark') },
        // { name: this.$t('invoice.status'), style: 'text-align:center' },
        // { name: this.$t('invoice.cashier') },
        // { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    },
    formatNumber: () => number => {
      if (number === undefined) return 0
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 3
      }).format(number)
    }
  },
  methods: {
    ...mapActions('invoice', ['destroy', 'getFormViewModel', 'updateOldNew']),
    onCloseDrawer() {
      this.visible = false
    },
    fetchData(attributes = {}) {
      this.loading = true
      this.$store
        .dispatch(
          'invoice/fetch',
          Object.assign({}, { branch_id: this.branch_id }, attributes)
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    async getBranch() {
      return this.$store
        .dispatch('user/getBranch', {
          filter: {
            user_branches: true
          }
        })
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en,
            companyId: el.company_id
          }))
        })
    },
    deleteRecord(record) {
      this.model.void_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('invoice.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.destroy({
            id: record.inv_id,
            data: this.model
          })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() { }
      })
    },
    show(record) {
      this.$store.commit('invoice/SET_SHOW_DATA', record)
      this.$router.push({ name: 'invoice-show', params: { id: record.inv_id } })
    },
    pdfView(record) {
      this.invoiceId = record.inv_id
      this.pdf_view = true
    },
    dateRangeChange(dateRange) {
      this.$store.commit(
        'invoice/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'invoice/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
      this.fetchData()
    },
    getExpiredDate(data) {
      let filter = data.filter(d => d.fee_type_id == 1)

      if (filter.length == 1) {
        return filter[0].fee_end_date
      } else if (filter.length == 2) {
        let latest = new Date(
          Math.max(
            ...filter.map(e => new Date(moment(e.fee_end_date, 'DD-MM-YYYY')))
          )
        )

        return moment(latest).format('DD-MM-YYYY')
      }

      return null
    },
    async onFetchCashier() {
      this.getFormViewModel({
        // asean use getFormViewModel
        fnName: 'cashierMakeInvoice'
      }).then(response => {
        this.cashiers = response.cashierMakeInvoice
      })
    },
    async showFilter() {
      this.visible = true
      this.waiting_fetch = true
      await Promise.all([this.getBranch(), this.onFetchCashier()])
      this.waiting_fetch = false
    },
    oldNewRowClick(row) {
      this.oldNewModel.inv_id = row.inv_id
      this.oldNewModel.is_new_student = row.is_new_student
      this.show_update_old_new = true
    },
    onUpdateOldNew() {
      this.waiting_update_old_new = true
      this.updateOldNew({
        id: this.oldNewModel.inv_id,
        data: this.oldNewModel
      })
        .then((response) => {
          this.$toasted.success(response.message)
          this.show_update_old_new = false
          this.fetchData({
            page: this.pagination.currentPage
          })
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting_update_old_new = false
        })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search, branch_id: this.branch_id })
    }, 500),
    is_external: debounce(function () {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
    created_by: debounce(function () {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
    filter_by_fee_type: debounce(function () {
      this.fetchData({ branch_id: this.branch_id })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('invoice/RESET_STATE')
    next()
  }
}
</script>
